import { useCallback } from 'react';
import type { FieldValues, FormState } from 'react-hook-form';
import type { BlockerFunction } from 'react-router';

import { useNavigateBlocker } from '@adflow/frontend/util-common';

import type { ModalDialogProps } from './ModalDialog';
import { ModalDialog } from './ModalDialog';

export interface WarnOnNavAwayProps extends Omit<
	ModalDialogProps,
	'show' | 'onCancel' | 'onConfirm'
> {
	formState: FormState<FieldValues>;
	blockNavigation?: boolean;
	resetForm?: () => void;
	/**
	 * Search param keys that should never trigger the discard warning on their
	 * own. Navigation that only changes these params (same pathname, all other
	 * params unchanged) is allowed through without prompting the user.
	 */
	ignoredSearchParams?: string[];
}

export function WarnOnNavAway({
	formState,
	resetForm,
	blockNavigation = false,
	ignoredSearchParams,
	...props
}: WarnOnNavAwayProps) {
	// formState.isDirty is triggered erroneously sometimes, so we use
	// formState.dirtyFields instead. See:
	// https://github.com/react-hook-form/react-hook-form/issues/10143
	const anyFieldsDirty = Object.keys(formState.dirtyFields).length > 0;
	const shouldBlock = useCallback<BlockerFunction>(
		({ currentLocation, nextLocation }) => {
			if (formState.isSubmitSuccessful && !blockNavigation) return false;

			// If ignored params are configured, check whether this navigation
			// only changes those params on the same pathname. If so, let it
			// through without prompting — e.g. switching between form sections
			// stored in the URL should never trigger the discard warning.
			if (
				ignoredSearchParams &&
				ignoredSearchParams.length > 0 &&
				currentLocation.pathname === nextLocation.pathname
			) {
				const strip = (search: string) => {
					const params = new URLSearchParams(search);
					for (const key of ignoredSearchParams) {
						params.delete(key);
					}
					return params.toString();
				};
				if (strip(currentLocation.search) === strip(nextLocation.search)) {
					return false;
				}
			}

			return anyFieldsDirty || blockNavigation;
		},
		[
			formState.isSubmitSuccessful,
			anyFieldsDirty,
			blockNavigation,
			ignoredSearchParams,
		]
	);
	const blocker = useNavigateBlocker(shouldBlock);

	return (
		<ModalDialog
			show={blocker.state === 'blocked'}
			onCancel={() => blocker.reset?.()}
			onConfirm={() => {
				resetForm?.();
				blocker.proceed?.();
			}}
			type="discard-changes"
			{...props}
		/>
	);
}
